import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface KanbanColumnProps {
  id: string;
  title: string;
  count: number;
  children: React.ReactNode;
  items: string[];
  variant?: "project" | "task";
  onAddClick?: () => void;
}

const taskStatusConfig: Record<string, { dotColor: string; dropBg: string }> = {
  todo: { 
    dotColor: "bg-slate-400", 
    dropBg: "bg-slate-500/20",
  },
  doing: { 
    dotColor: "bg-blue-500", 
    dropBg: "bg-blue-500/20",
  },
  done: { 
    dotColor: "bg-emerald-500", 
    dropBg: "bg-emerald-500/20",
  },
};

const projectStatusConfig: Record<string, { dotColor: string; dropBg: string }> = {
  backlog: { 
    dotColor: "bg-slate-400", 
    dropBg: "bg-slate-500/20",
  },
  in_progress: { 
    dotColor: "bg-blue-500", 
    dropBg: "bg-blue-500/20",
  },
  review: { 
    dotColor: "bg-amber-500", 
    dropBg: "bg-amber-500/20",
  },
  done: { 
    dotColor: "bg-emerald-500", 
    dropBg: "bg-emerald-500/20",
  },
};

export function KanbanColumn({ id, title, count, children, items, variant = "task", onAddClick }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const config = variant === "task" ? taskStatusConfig : projectStatusConfig;
  const statusConfig = config[id] || taskStatusConfig.todo;

  return (
    <div 
      ref={setNodeRef}
      className={cn(
        "flex flex-col rounded-xl bg-muted/50 dark:bg-slate-900/80 border border-border/50 transition-all duration-200 min-h-[400px]",
        isOver && cn("ring-2 ring-offset-2 ring-offset-background shadow-lg", statusConfig.dropBg)
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3">
        <span className={cn("h-2.5 w-2.5 rounded-full flex-shrink-0", statusConfig.dotColor)} />
        <h3 className="font-medium text-foreground">{title}</h3>
        <span className="text-muted-foreground text-sm">{count}</span>
      </div>
      
      {/* Drop Zone */}
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div
          className={cn(
            "flex-1 space-y-2 px-3 pb-2 transition-all duration-200",
            isOver && statusConfig.dropBg
          )}
        >
          {children}
        </div>
      </SortableContext>

      {/* Add Task Button */}
      {onAddClick && (
        <button
          onClick={onAddClick}
          className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors rounded-b-xl border-t border-border/30"
        >
          <Plus className="h-4 w-4" />
          <span>Adicionar tarefa</span>
        </button>
      )}
    </div>
  );
}
